import { SOW, CustomerInfo } from '../models/sow';
import { handleResponse } from './errorHandler';
import AccessTokenManager from './accessTokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const sowService = {
  async createSOW(address: string): Promise<SOW> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };
      
      console.log('Headers being sent:', headers);
      
      const response = await fetch(`${API_BASE_URL}/statementofwork`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          address,
        }),
      });

      const data = await handleResponse(response);
      return data.sow;
    } catch (error) {
      console.error('SOW service error:', error);
      throw error;
    }
  },

  async getSOW(sowId: string, requireAuth: boolean = true): Promise<SOW> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}`, {
        method: 'GET',
        headers,
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW service error:', error);
      throw error;
    }
  },

  async updateSOW(sowId: string, updateData: Partial<SOW>, requireAuth: boolean = true): Promise<SOW> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updateData),
      });

      const data = await handleResponse(response);
      return data.sow;
    } catch (error) {
      console.error('SOW service error:', error);
      throw error;
    }
  },

  async getSOWRegistry(): Promise<{ sowRegistry: Array<{ id: string; address: string; status: string; date: string }> }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      const response = await fetch(`${API_BASE_URL}/statementofwork`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('SOW service error:', error);
      throw error;
    }
  },

  async uploadImageForAnalysis(sowId: string, file: File, requireAuth: boolean = true): Promise<string> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const headers: Record<string, string> = {};

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Always use SOW upload — registers image on the SOW
      const endpoint = `${API_BASE_URL}/statementofwork/${sowId}/upload`;

      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData,
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const uploadResult = await response.json();
      return uploadResult.id;
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  },

  async analyzeSOWImage(sowId: string, imageId: string, projectType: string, questionnaireContext?: { type: string; questionId?: string; questionnaireId?: string; component?: string }, requireAuth: boolean = true): Promise<any> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/image/analyze`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          imageId: imageId,
          projectType: projectType,
          questionnaireContext: questionnaireContext
        })
      });

      const data = await response.json();

      // Handle validation errors (400 status with invalid_image)
      if (!response.ok) {
        if (response.status === 400 && data.status === 'invalid_image') {
          // Return validation error as part of response
          return data;
        }
        throw new Error(data.error || 'Failed to analyze image');
      }

      return data;
    } catch (error) {
      console.error('Image analysis error:', error);
      throw error;
    }
  },

  async getSOWImages(sowId: string, requireAuth: boolean = true): Promise<{ images: any[] }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/images`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error('Failed to get SOW images');
      }

      return await response.json();
    } catch (error) {
      console.error('Get SOW images error:', error);
      throw error;
    }
  },

  async bulkClassifyImages(sowId: string, imageIds: string[], requireAuth: boolean = true): Promise<{
    sowId: string;
    classified: number;
    total: number;
    results: Array<{
      imageId: string;
      jobId: string;
      jobLabel: string;
      confidence: number;
      description: string;
    }>;
    byJob: Array<{
      jobId: string;
      jobLabel: string;
      images: Array<{
        imageId: string;
        jobId: string;
        jobLabel: string;
        confidence: number;
        description: string;
      }>;
    }>;
  }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/images/bulk-classify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ imageIds })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to classify images');
      }

      return await response.json();
    } catch (error) {
      console.error('Bulk classify images error:', error);
      throw error;
    }
  },

  async generateScopeFromImages(sowId: string, requireAuth: boolean = true, imageIds: string[] = []): Promise<{
    sowId: string;
    projectType: string;
    description: string;
    jobs: Array<{ id: string; type: string; label: string; checklistType: string }>;
    recommendedAnswers: Record<string, { checkedItems: string[]; reasoning: string }>;
    imageAssignments: Record<string, string>;
  }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/images/generate-scope`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ imageIds })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate scope from images');
      }

      return await response.json();
    } catch (error) {
      console.error('Generate scope from images error:', error);
      throw error;
    }
  },

  async confirmScope(sowId: string, userNotes: string = '', requireAuth: boolean = true): Promise<any> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) throw new Error('No auth token available');
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/scope/confirm`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ userNotes })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to confirm scope');
      }

      return await response.json();
    } catch (error) {
      console.error('Confirm scope error:', error);
      throw error;
    }
  },

  async addImageToJob(sowId: string, imageId: string, jobId: string, requireAuth: boolean = true): Promise<any> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/images/add-to-job`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ imageId, jobId })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add image to job');
      }

      return await response.json();
    } catch (error) {
      console.error('Add image to job error:', error);
      throw error;
    }
  },

  async assessImageQuality(sowId: string, imageId: string, questionId: string, questionText: string, attemptNumber: number = 1, requireAuth: boolean = true): Promise<any> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/image/${imageId}/assess`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          questionId,
          questionText,
          attemptNumber
        })
      });

      if (!response.ok) {
        throw new Error('Failed to assess image quality');
      }

      const result = await response.json();
      return result.assessment;
    } catch (error) {
      console.error('Image quality assessment error:', error);
      throw error;
    }
  },

  async getQuestionnaireQuestions(sowId: string, questionnaireId: string): Promise<{ 
    questions: any[]; 
    filteredStats: { originalCount: number; filteredCount: number; skippedCount: number }; 
    questionnaireId: string; 
    questionnaireName: string 
  }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      
      if (!token) {
        throw new Error('No auth token available');
      }
      
      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/questionnaire/${questionnaireId}/questions`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get questionnaire questions');
      }

      return await response.json();
    } catch (error) {
      console.error('Get questionnaire questions error:', error);
      throw error;
    }
  },

  async generateQuickEstimate(sowId: string, requireAuth: boolean = true): Promise<{
    totalCostLow: number;
    totalCostHigh: number;
    totalLaborLow: number;
    totalLaborHigh: number;
    totalMaterialLow: number;
    totalMaterialHigh: number;
    breakdown: Array<{
      category: string;
      laborCostLow: number;
      laborCostHigh: number;
      materialCostLow: number;
      materialCostHigh: number;
      totalLow: number;
      totalHigh: number;
    }>;
    notes?: string;
  }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (!token) {
          throw new Error('No auth token available');
        }
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/quick-estimate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate quick estimate');
      }

      const result = await response.json();
      // Backend returns { status, estimate, bidEstimate }
      // Attach bidEstimate to the estimate object so the caller gets both
      const estimate = result.estimate || result;
      if (result.bidEstimate) {
        estimate.bidEstimate = result.bidEstimate;
      }
      return estimate;
    } catch (error) {
      console.error('Generate quick estimate error:', error);
      throw error;
    }
  },

  async generateProjectEstimate(sowId: string): Promise<{
    status: string;
    projectPlan: any;
    summary: {
      totalWorkItems: number;
      completedWorkItems: number;
      estimatedCostRange: { low: number; high: number };
      confidence: number;
    };
  }> {
    try {
      const token = AccessTokenManager.getAccessToken();

      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate project estimate');
      }

      return await response.json();
    } catch (error) {
      console.error('Generate project estimate error:', error);
      throw error;
    }
  },

  // Public SOW creation endpoint
  async createPublicSOW(alias: string, customerInfo: CustomerInfo): Promise<{ sowId: string }> {
    try {
      const response = await fetch(`${API_BASE_URL}/statementofwork/public/${alias}/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(customerInfo),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Public SOW creation error:', error);
      throw error;
    }
  },

  async analyzeProjectDescription(sowId: string, description: string, requireAuth: boolean = true): Promise<{
    projectType: string;
    components: string[];
    photoInstructions: Array<{
      id: string;
      title: string;
      helpText: string;
      category: string;
      targetDatapoints: string[];
    }>;
  }> {
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (requireAuth) {
        const token = AccessTokenManager.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/analyze-description`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ description }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Analyze description error:', error);
      throw error;
    }
  },

  async toggleWorkItem(sowId: string, jobIndex: number, workItemIndex: number): Promise<{ quickEstimate: any; bidEstimate: any }> {
    try {
      const token = AccessTokenManager.getAccessToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      const response = await fetch(`${API_BASE_URL}/statementofwork/${sowId}/estimate/work-item/toggle`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ jobIndex, workItemIndex }),
      });

      return await handleResponse(response);
    } catch (error) {
      console.error('Toggle work item error:', error);
      throw error;
    }
  },
};